// @flow
import {cookieOptions} from './cookieUtils';
import type {HapiCookieState} from './cookieUtils';
import type {SearchRequest} from '@homeaway/search-url-parser';
import {pick, uniq, isEmpty, isEqual, sortedUniq, isNil} from 'lodash';

export type TripCookie = {
    arrival?: string,
    departure?: string,
    numberOfAdults?: number,
    numberOfChildren?: number,
    petIncluded?: boolean
};

export const emptyCookie: TripCookie = {
    arrival: '',
    departure: '',
    numberOfAdults: 0,
    numberOfChildren: 0,
    petIncluded: false
};

export type TripQuery = {
    arrival?: string,
    departure?: string,
    adultsCount?: number,
    childrenCount?: number,
    petIncluded?: boolean
};

export const emptyTripQuery: TripQuery = {
    arrival: '',
    departure: '',
    petIncluded: false
};

export const TRIPDATA_COOKIE = 'ha-trip-prst';

export const tripDataFields: string[] = [
    'arrival',
    'departure',
    'adultsCount',
    'childrenCount',
    'petIncluded'
];

const tripValuesAreEquivalent = (aValue: mixed, bValue: mixed, key: string) => {
    if (Array.isArray(aValue) && Array.isArray(bValue)) {
        const a = sortedUniq(aValue.sort());
        const b = sortedUniq(bValue.sort());
        return isEqual(a, b);
    }
    return (
        (isNil(bValue) && (isNil(aValue) || aValue === emptyTripQuery[key])) ||
        (isNil(aValue) && (isNil(bValue) || bValue === emptyTripQuery[key])) ||
        bValue === aValue
    );
};

export const requestsHaveEquivalentTripData = (
    a?: SearchRequest,
    b?: SearchRequest
): boolean => {
    const aPicked = pick(a || {}, ['filter', ...tripDataFields]);
    const bPicked = pick(b || {}, ['filter', ...tripDataFields]);

    const aKeys = Object.keys(aPicked);
    const bKeys = Object.keys(bPicked);
    const allKeys = uniq([...aKeys, ...bKeys]);

    for (let i = 0; i < allKeys.length; i++) {
        const bValue = bPicked[allKeys[i]];
        const aValue = aPicked[allKeys[i]];

        if (tripValuesAreEquivalent(aValue, bValue, allKeys[i])) {
            continue;
        }
        return false;
    }

    return true;
};

export const resolvePositiveNumber = (count?: ?number): number => {
    const number = (count && parseInt(count, 10)) || 0;
    return (number > 0 && number) || 0;
};

export const isTrue = (value?: ?boolean | ?string): boolean => {
    if (value) {
        return value.toString() === 'true';
    }

    return false;
};

export const tripQueryFromTripCookie = (cookie: TripCookie): TripQuery => {
    if (isEmpty(cookie)) {
        return {};
    }
    const adultsCount =
        cookie.numberOfAdults && resolvePositiveNumber(cookie.numberOfAdults);
    const childrenCount =
        cookie.numberOfChildren &&
        resolvePositiveNumber(cookie.numberOfChildren);
    const petIncluded = cookie.petIncluded && isTrue(cookie.petIncluded);
    const arrival = cookie.arrival;
    const departure = cookie.departure;

    return {
        adultsCount,
        childrenCount,
        petIncluded,
        arrival,
        departure
    };
};

export const cookieDataFromQueryData = (query: TripQuery): TripCookie => {
    const numberOfAdults =
        query.adultsCount && resolvePositiveNumber(query.adultsCount);
    const numberOfChildren =
        query.childrenCount && resolvePositiveNumber(query.childrenCount);
    const petIncluded = query.petIncluded && isTrue(query.petIncluded);
    const arrival = query.arrival;
    const departure = query.departure;

    return {
        numberOfAdults,
        numberOfChildren,
        petIncluded,
        arrival,
        departure
    };
};

export const tripDataCookieFromSearchRequest = (
    searchRequest: SearchRequest
): HapiCookieState => {
    // write the trip data from query params to the trip data cookie

    const queryTripData: TripQuery = pick(searchRequest, tripDataFields);
    return {
        name: TRIPDATA_COOKIE,
        data: encodeURIComponent(
            JSON.stringify(cookieDataFromQueryData(queryTripData))
        ),
        options: cookieOptions
    };
};
